import React, { useState, useEffect, useRef } from "react";
import { SITENAMEALIAS } from "../utils/init";
import { Link } from "react-router-dom";
import {
  showToast,
  showHttpError,
  currentPageAccess,
  handleDynamicCurrencyDetiails,
} from "../utils/library";
import PageHeader from "../components/PageHeader";
import {
  FetchUsdBtcLedgerAmount,
  DashboardPaymentHistory,
  FetchMerchantStatus,
  GetUserDetails,
  GetTransactionsByFilter,
} from "../utils/service";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import Footer from "../components/Footer";
import Loader from "../components/Loader";

/* ─── Shared helpers (mirrors Transactions.jsx exactly) ────────────────────── */
const cap = (s) => (s ? s[0].toUpperCase() + s.slice(1) : s);

const mapStatus = (s) => {
  if (!s) return "pending";
  const ll = s.toLowerCase();
  if (
    [
      "success",
      "succeeded",
      "payment_confirmed",
      "closed_with_autometic_overpayment",
      "closed_with_exact_payment",
      "closed_with_manual_overpayment",
    ].includes(ll)
  )
    return "succeeded";
  if (
    [
      "failed",
      "invoice_expired",
      "payment_failed",
      "closed_with_underpayment",
    ].includes(ll)
  )
    return "failed";
  if (["refunded", "refund_approved", "refund_requested"].includes(ll))
    return "refunded";
  if (
    [
      "payment_created",
      "payment_processing",
      "payment_initiated",
      "waiting",
      "processing",
    ].includes(ll)
  )
    return "processing";
  return "pending";
};

const statusLabel = (s) => {
  if (!s) return "Processing";
  const labels = {
    payment_created: "Payment Created",
    payment_processing: "Payment Processing",
    payment_initiated: "Payment Initiated",
    payment_confirmed: "Payment Confirmed",
    closed_with_autometic_overpayment: "Closed with Overpayment",
    closed_with_exact_payment: "Closed",
    closed_with_manual_overpayment: "Closed with Overpayment",
    closed_with_underpayment: "Closed with Underpayment",
    invoice_expired: "Expired",
    payment_failed: "Failed",
    refunded: "Refunded",
    refund_approved: "Refund Approved",
    refund_requested: "Refund Requested",
    succeeded: "Succeeded",
    success: "Success",
    failed: "Failed",
    pending: "Pending",
    processing: "Processing",
  };
  return labels[s.toLowerCase()] || cap(s.replace(/_/g, " "));
};

const fmtAmt = (amount, currency) => {
  if (amount == null || isNaN(amount)) return "—";
  const num = Number(amount);
  const sym =
    currency === "USD"
      ? "$"
      : currency === "EUR"
      ? "€"
      : currency === "GBP"
      ? "£"
      : "";
  const suffix = ["USD", "EUR", "GBP"].includes(currency)
    ? ""
    : " " + (currency || "");
  if (Math.abs(num) < 0.0001 && num !== 0)
    return `${sym}${num.toFixed(8)}${suffix}`;
  return `${sym}${num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 8,
  })}${suffix}`;
};

const fmtDate = (dateStr) => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

/* ─── Styles ───────────────────────────────────────────────────────────────── */
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Bricolage+Grotesque:wght@700;800&display=swap');

  .an-page {
    --an-bg:           var(--body-color);
    --an-surface:      var(--body-color-less);
    --an-surface-2:    var(--body-color-less2);
    --an-surface-3:    var(--body-color-less3);
    --an-border:       var(--body-color-less2);
    --an-border-soft:  var(--body-color-less2);
    --an-text-primary:   var(--text-color);
    --an-text-secondary: var(--text-color);
    --an-text-muted:     var(--text-color);
    --an-accent:       var(--btn-color);
    --an-accent-hover: var(--navbar-color-less);
    --an-accent-soft:  color-mix(in srgb, var(--btn-color), transparent 88%);
    --an-accent-glow:  color-mix(in srgb, var(--btn-color), transparent 78%);
    --an-green:      #059669;
    --an-green-soft: rgba(5,150,105,0.12);
    --an-amber:      #d97706;
    --an-amber-soft: rgba(217,119,6,0.12);
    --an-red:        #dc2626;
    --an-red-soft:   rgba(220,38,38,0.12);
    --an-blue:       #2563eb;
    --an-blue-soft:  rgba(37,99,235,0.12);
    --an-shadow-sm:  0 1px 2px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04);
    --an-shadow:     0 2px 8px rgba(0,0,0,0.08), 0 4px 16px rgba(0,0,0,0.05);
    --an-shadow-lg:  0 8px 32px rgba(0,0,0,0.12);
    --an-shadow-btn: color-mix(in srgb, var(--btn-color), transparent 70%);
    --an-radius:    14px;
    --an-radius-sm: 8px;
    --an-font:         'Plus Jakarta Sans', sans-serif;
    --an-font-display: 'Bricolage Grotesque', sans-serif;
    --an-grad-btn:  linear-gradient(135deg, var(--btn-color) 0%, var(--navbar-color-less2) 100%);
    --an-grad-hero: linear-gradient(135deg, var(--navbar-color) 0%, var(--btn-color) 60%, var(--navbar-color-less2) 100%);
    --an-grad-blue: linear-gradient(90deg, var(--btn-color), var(--navbar-color-less2));
    --an-grad-green:linear-gradient(90deg, #059669, #10b981);
    --an-grad-gold: linear-gradient(90deg, #d97706, #f59e0b);
  }

  .an-page {
    font-family: var(--an-font);
    background: var(--an-bg);
    min-height: 100vh;
    padding: 28px 32px 56px;
    color: var(--an-text-primary);
    transition: background 0.3s, color 0.3s;
  }

  @keyframes an-fadeUp {
    from { opacity: 0; transform: translateY(12px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes an-fadeIn { from { opacity: 0; } to { opacity: 1; } }
  @keyframes an-scaleIn { from { opacity: 0; transform: scale(0.97); } to { opacity: 1; transform: scale(1); } }
  @keyframes an-ping {
    0%   { transform: scale(1); opacity: 0.4; }
    70%  { transform: scale(2.2); opacity: 0; }
    100% { transform: scale(2.2); opacity: 0; }
  }
  @keyframes an-grow-bar { from { width: 0; } to { width: var(--bar-w); } }

  .an-page-header {
    display: flex; align-items: flex-start; justify-content: space-between;
    margin-bottom: 28px; flex-wrap: wrap; gap: 14px;
    animation: an-fadeUp 0.45s ease both;
  }
  .an-page-title {
    font-family: var(--an-font-display); font-size: 26px; font-weight: 800;
    letter-spacing: -0.6px; color: var(--an-text-primary);
    margin: 0 0 4px; line-height: 1.15;
  }
  .an-page-subtitle { font-size: 13px; color: var(--an-text-secondary); margin: 0; }
  .an-header-right { display: flex; align-items: center; gap: 10px; }
  .an-date-badge {
    background: var(--an-surface); border: 1px solid var(--an-border);
    border-radius: var(--an-radius-sm); padding: 8px 14px;
    font-size: 12.5px; color: var(--an-text-secondary); font-weight: 600;
    box-shadow: var(--an-shadow-sm); display: flex; align-items: center; gap: 8px;
  }
  .an-live-dot {
    width: 7px; height: 7px; border-radius: 50%;
    background: var(--an-green); display: inline-block; position: relative;
  }
  .an-live-dot::after {
    content: ''; position: absolute; inset: -3px; border-radius: 50%;
    background: var(--an-green); opacity: 0.35;
    animation: an-ping 2s ease-in-out infinite;
  }

  /* ── Metric Cards ── */
  .an-metrics-grid {
    display: grid; grid-template-columns: repeat(3, 1fr);
    gap: 16px; margin-bottom: 20px;
  }
  @media (max-width: 1100px) { .an-metrics-grid { grid-template-columns: repeat(2,1fr); } }
  @media (max-width: 640px)  { .an-metrics-grid { grid-template-columns: 1fr; } }

  .an-metric-card {
    background: var(--an-surface); border: 1px solid var(--an-border);
    border-radius: var(--an-radius); padding: 24px 24px 20px;
    box-shadow: var(--an-shadow-sm); position: relative; overflow: hidden;
    cursor: default; transition: transform 0.22s cubic-bezier(.34,1.5,.64,1), box-shadow 0.22s;
    animation: an-fadeUp 0.45s ease both;
  }
  .an-metric-card:nth-child(1) { animation-delay: 0.05s; }
  .an-metric-card:nth-child(2) { animation-delay: 0.11s; }
  .an-metric-card:nth-child(3) { animation-delay: 0.17s; }
  .an-metric-card:hover { transform: translateY(-3px); box-shadow: var(--an-shadow-lg); }
  .an-metric-card::before {
    content: ''; position: absolute; top: 0; left: 0; right: 0; height: 3px;
    border-radius: var(--an-radius) var(--an-radius) 0 0;
  }
  .an-metric-card.blue::before  { background: var(--an-grad-blue); }
  .an-metric-card.green::before { background: var(--an-grad-green); }
  .an-metric-card.gold::before  { background: var(--an-grad-gold); }
  .an-metric-top { margin-bottom: 16px; }
  .an-metric-icon {
    width: 44px; height: 44px; border-radius: 11px;
    display: flex; align-items: center; justify-content: center;
    font-size: 18px; flex-shrink: 0;
    transition: transform 0.22s cubic-bezier(.34,1.5,.64,1);
  }
  .an-metric-card:hover .an-metric-icon { transform: scale(1.08) rotate(-3deg); }
  .an-metric-icon.blue  { background: var(--an-accent-soft); color: var(--an-accent); }
  .an-metric-icon.green { background: var(--an-green-soft);  color: var(--an-green); }
  .an-metric-icon.gold  { background: var(--an-amber-soft);  color: var(--an-amber); }
  .an-metric-label {
    font-size: 11.5px; font-weight: 800; text-transform: uppercase;
    letter-spacing: 0.9px; color: var(--an-text-secondary); margin-bottom: 6px;
  }
  .an-metric-value {
    font-family: var(--an-font-display); font-size: 36px; font-weight: 800;
    letter-spacing: -1.5px; color: var(--an-text-primary); line-height: 1; margin-bottom: 14px;
  }
  .an-metric-footer {
    display: flex; align-items: center; justify-content: space-between;
    padding-top: 12px; border-top: 1px solid var(--an-border);
  }
  .an-metric-badge {
    display: inline-flex; align-items: center; gap: 4px;
    padding: 3px 9px; border-radius: 20px; font-size: 11.5px; font-weight: 700;
  }
  .an-metric-badge.up      { background: var(--an-green-soft); color: var(--an-green); }
  .an-metric-badge.neutral { background: var(--an-accent-soft); color: var(--an-accent); }
  .an-metric-sub { font-size: 11.5px; color: var(--an-text-muted); }

  /* ── Generic Card ── */
  .an-card {
    background: var(--an-surface); border: 1px solid var(--an-border);
    border-radius: var(--an-radius); box-shadow: var(--an-shadow-sm);
    overflow: hidden; animation: an-fadeUp 0.45s ease both; transition: box-shadow 0.22s;
  }
  .an-card:hover { box-shadow: var(--an-shadow); }
  .an-card-header {
    padding: 16px 22px; border-bottom: 1px solid var(--an-border);
    display: flex; align-items: center; justify-content: space-between;
  }
  .an-card-title {
    font-family: var(--an-font-display); font-size: 15px; font-weight: 700;
    color: var(--an-text-primary); margin: 0; display: flex; align-items: center; gap: 10px;
  }
  .an-card-title-icon {
    width: 28px; height: 28px; border-radius: 7px;
    background: var(--an-accent-soft); color: var(--an-accent);
    display: flex; align-items: center; justify-content: center; font-size: 12px;
  }
  .an-card-body { padding: 18px 22px; }

  /* ── Action Center ── */
  .an-action-grid {
    display: grid; grid-template-columns: repeat(3, 1fr);
    gap: 16px; margin-bottom: 20px;
  }
  @media (max-width: 1150px) { .an-action-grid { grid-template-columns: 1fr; } }
  .an-action-buttons { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
  @media (max-width: 640px) { .an-action-buttons { grid-template-columns: 1fr; } }

  .an-action-btn {
    display: flex; align-items: center; gap: 12px; padding: 14px 16px;
    border-radius: 10px; border: 1px solid var(--an-border);
    background: var(--an-surface-2); text-decoration: none !important;
    color: var(--an-text-primary) !important; transition: all 0.2s ease;
    position: relative; overflow: hidden; cursor: pointer;
  }
  .an-action-btn::before {
    content: ''; position: absolute; top: 0; left: -100%; width: 60%; height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.06), transparent);
    transition: left 0.45s ease;
  }
  .an-action-btn:hover::before { left: 150%; }
  .an-action-btn:hover {
    border-color: var(--an-accent); background: var(--an-surface);
    color: var(--an-accent) !important; transform: translateY(-1px);
    box-shadow: 0 4px 16px var(--an-accent-glow);
  }
  .an-action-btn:hover .an-abt-icon { background: var(--an-accent); color: #fff; }
  .an-abt-icon {
    width: 40px; height: 40px; border-radius: 9px;
    background: var(--an-accent-soft); color: var(--an-accent);
    display: flex; align-items: center; justify-content: center;
    font-size: 16px; flex-shrink: 0; transition: all 0.2s ease;
  }
  .an-abt-text { flex: 1; min-width: 0; }
  .an-abt-label { font-size: 13px; font-weight: 700; line-height: 1.2; }
  .an-abt-sub   { font-size: 11.5px; color: var(--an-text-secondary); margin-top: 2px; }
  .an-abt-arrow {
    width: 26px; height: 26px; border-radius: 7px; background: var(--an-surface-3);
    display: flex; align-items: center; justify-content: center;
    font-size: 11px; color: var(--an-text-muted); flex-shrink: 0; transition: all 0.2s;
  }
  .an-action-btn:hover .an-abt-arrow {
    background: var(--an-accent-soft); color: var(--an-accent); transform: translateX(2px);
  }

  /* ── Account Status ── */
  .an-status-list { display: flex; flex-direction: column; }
  .an-status-row {
    display: flex; align-items: center; justify-content: space-between;
    padding: 13px 0; gap: 12px; border-bottom: 1px solid var(--an-border);
  }
  .an-status-row:last-child { border-bottom: none; }
  .an-status-label {
    display: flex; align-items: center; gap: 8px;
    font-size: 12.5px; color: var(--an-text-secondary); font-weight: 500; flex-shrink: 0;
  }
  .an-status-label-icon {
    width: 22px; height: 22px; border-radius: 6px; background: var(--an-surface-2);
    display: flex; align-items: center; justify-content: center;
    font-size: 10px; color: var(--an-text-muted);
  }
  .an-status-value {
    font-size: 12.5px; font-weight: 600; color: var(--an-text-primary);
    text-align: right; display: flex; align-items: center; gap: 6px;
    flex-wrap: wrap; justify-content: flex-end; min-width: 0;
  }
  .an-progress-wrap {
    display: flex; flex-direction: column; align-items: flex-end; gap: 5px; min-width: 0;
  }

  /* ── Badges ── */
  .an-badge {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 3px 10px; border-radius: 20px; font-size: 11.5px; font-weight: 700;
    word-break: break-word; white-space: normal; text-align: right;
  }
  .an-badge.active  { background: var(--an-green-soft); color: var(--an-green); }
  .an-badge.warning { background: var(--an-amber-soft); color: var(--an-amber); }
  .an-badge.info    { background: var(--an-accent-soft); color: var(--an-accent); }
  .an-badge-dot { width: 6px; height: 6px; border-radius: 50%; background: currentColor; }

  .an-link-btn {
    font-size: 12px; font-weight: 700; color: #fff;
    background: var(--an-accent); border: none; border-radius: 7px;
    padding: 6px 13px; cursor: pointer; text-decoration: none;
    transition: all 0.18s ease; white-space: nowrap;
    display: inline-flex; align-items: center; gap: 5px;
    box-shadow: var(--an-shadow-btn);
  }
  .an-link-btn:hover {
    background: var(--an-accent-hover); transform: translateY(-1px);
    box-shadow: 0 6px 20px var(--an-accent-glow); color: #fff;
  }
  .an-link-btn.done {
    background: var(--an-green); box-shadow: 0 4px 14px var(--an-green-soft); color: #fff;
  }
  .an-link-btn.done:hover {
    background: #047857; box-shadow: 0 6px 20px var(--an-green-soft);
    transform: translateY(-1px); color: #fff;
  }

  /* ── Recent Transactions ── */
  .an-rtx-tabs {
    display: flex; gap: 4px; flex-wrap: wrap; margin-bottom: 14px;
  }
  .an-rtx-tab {
    height: 30px; padding: 0 13px; border-radius: 7px;
    border: 1px solid transparent; background: transparent;
    font-family: var(--an-font); font-size: 12px; font-weight: 600;
    color: var(--an-text-secondary); opacity: .55; cursor: pointer;
    transition: all .15s; white-space: nowrap;
    display: flex; align-items: center; gap: 5px;
  }
  .an-rtx-tab:hover { background: var(--an-surface-2); opacity: .85; }
  .an-rtx-tab.active {
    background: var(--an-accent-soft); border-color: rgba(29,78,216,.25);
    color: var(--an-accent); opacity: 1;
  }
  .an-rtx-tab-count {
    font-size: 10px; background: var(--an-accent-soft);
    color: var(--an-accent); border-radius: 10px;
    padding: 1px 6px; font-weight: 700;
  }
  .an-rtx-tab.active .an-rtx-tab-count { background: rgba(29,78,216,.2); }
  .an-rtx-tab.test.active {
    background: rgba(16,185,129,.12); border-color: rgba(16,185,129,.3);
    color: var(--an-green);
  }
  .an-rtx-tab.test.active .an-rtx-tab-count {
    background: rgba(16,185,129,.2); color: var(--an-green);
  }

  .an-rtx-search-wrap { position: relative; flex: 1; min-width: 200px; }
  .an-rtx-search-icon {
    position: absolute; left: 11px; top: 50%;
    transform: translateY(-50%); color: var(--an-text-muted);
    font-size: 13px; pointer-events: none;
  }
  .an-rtx-search {
    width: 100%; height: 34px; padding: 0 0 0 34px;
    background: var(--an-surface-2); border: 1px solid var(--an-border);
    border-radius: 8px; color: var(--an-text-primary);
    font-family: var(--an-font); font-size: 13px; outline: none;
    transition: border-color .2s, box-shadow .2s;
  }
  .an-rtx-search::placeholder { color: var(--an-text-muted); }
  .an-rtx-search:focus {
    border-color: var(--an-accent);
    box-shadow: 0 0 0 3px var(--an-accent-soft);
  }
  .an-rtx-search-clear {
    position: absolute; right: 9px; top: 50%;
    transform: translateY(-50%); background: none;
    border: none; color: var(--an-text-muted);
    cursor: pointer; font-size: 12px; padding: 3px 6px; border-radius: 4px;
  }
  .an-rtx-search-clear:hover { background: var(--an-surface-3); color: var(--an-text-primary); }

  .an-rtx-table-wrap { overflow-x: auto; overflow-y: hidden; -webkit-overflow-scrolling: touch; }
  .an-rtx-table { width: 100%; border-collapse: collapse; font-size: 13px; min-width: 700px; }
  .an-rtx-table thead tr th {
    padding: 10px 16px; text-align: left; font-size: 10.5px; font-weight: 800;
    text-transform: uppercase; letter-spacing: 0.8px; color: var(--an-text-muted);
    background: var(--an-surface-2); border-bottom: 1px solid var(--an-border); white-space: nowrap;
  }
  .an-rtx-table tbody tr {
    border-bottom: 1px solid var(--an-border);
    transition: background .13s; animation: an-fadeIn 0.35s ease both;
    cursor: pointer;
  }
  .an-rtx-table tbody tr:last-child { border-bottom: none; }
  .an-rtx-table tbody tr:hover { background: var(--an-surface-2); }
  .an-rtx-table tbody td { padding: 13px 16px; vertical-align: middle; }

  .an-rtx-txnid {
    font-family: 'SF Mono', 'Fira Code', monospace; font-size: 11.5px;
    color: var(--an-text-secondary); background: var(--an-surface-2);
    padding: 3px 7px; border-radius: 5px; border: 1px solid var(--an-border);
  }
  .an-rtx-tag {
    display: inline-block; padding: 2px 8px; border-radius: 5px;
    font-size: 10px; font-weight: 600; margin-top: 3px;
  }
  .an-rtx-tag.sub     { background: rgba(139,92,246,.1); color:#a78bfa; border:1px solid rgba(139,92,246,.15); }
  .an-rtx-tag.onetime { background: rgba(59,130,246,.08); color:#60a5fa; border:1px solid rgba(59,130,246,.15); }
  .an-rtx-tag.test    { background: rgba(100,116,139,.1); opacity:.6; border:1px solid rgba(100,116,139,.15); color: var(--an-text-secondary); }

  .an-rtx-product { font-weight: 600; font-size: 13px; color: var(--an-text-primary); }
  .an-rtx-amount  { font-weight: 700; font-size: 13.5px; font-variant-numeric: tabular-nums; color: var(--an-text-primary); }
  .an-rtx-amtsub  { font-size: 11px; color: var(--an-text-muted); margin-top: 2px; }
  .an-rtx-method  { font-weight: 600; font-size: 13px; color: var(--an-text-primary); }
  .an-rtx-date    { font-size: 12px; color: var(--an-text-muted); white-space: nowrap; }

  .an-rtx-arrow {
    width: 28px; height: 28px; border-radius: 7px;
    background: var(--an-accent-soft); border: 1px solid rgba(29,78,216,.15);
    color: var(--an-accent); font-size: 14px;
    display: flex; align-items: center; justify-content: center;
    transition: all .18s; flex-shrink: 0;
  }
  .an-rtx-table tbody tr:hover .an-rtx-arrow {
    background: var(--an-accent); color: #fff;
  }

  /* Status pills */
  .an-rtx-pill {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 4px 10px; border-radius: 20px;
    font-size: 11px; font-weight: 700; white-space: nowrap;
  }
  .an-rtx-pill::before {
    content: ''; width: 6px; height: 6px;
    border-radius: 50%; flex-shrink: 0; background: currentColor;
  }
  .an-rtx-pill.succeeded { background: var(--an-green-soft); color: var(--an-green); }
  .an-rtx-pill.failed    { background: var(--an-red-soft);   color: var(--an-red); }
  .an-rtx-pill.processing,
  .an-rtx-pill.pending   { background: var(--an-amber-soft); color: var(--an-amber); }
  .an-rtx-pill.refunded  { background: var(--an-accent-soft);color: var(--an-accent); }

  /* Empty / Loading */
  .an-rtx-empty {
    text-align: center; padding: 48px 20px;
    color: var(--an-text-muted); font-size: 13.5px;
  }
  .an-rtx-empty i { font-size: 32px; display: block; margin-bottom: 10px; opacity: .3; }

  /* View all footer */
  .an-rtx-footer {
    display: flex; align-items: center; justify-content: space-between;
    padding: 13px 22px; border-top: 1px solid var(--an-border);
    background: var(--an-surface-2);
  }
  .an-rtx-count { font-size: 12px; color: var(--an-text-muted); font-weight: 500; }
  .an-rtx-viewall {
    font-size: 13px; font-weight: 700; color: #fff; text-decoration: none;
    display: inline-flex; align-items: center; gap: 6px;
    padding: 7px 16px; border-radius: 8px; background: var(--an-accent);
    box-shadow: var(--an-shadow-btn); transition: all .18s;
  }
  .an-rtx-viewall:hover {
    background: var(--an-accent-hover); color: #fff;
    transform: translateY(-1px); box-shadow: 0 6px 20px var(--an-accent-glow);
  }

  /* ── Drawer ── */
  .an-dr-ov {
    position: fixed; inset: 0; background: rgba(0,0,0,.5);
    backdrop-filter: blur(4px); z-index: 1050;
    opacity: 0; pointer-events: none; transition: opacity .25s;
  }
  .an-dr-ov.open { opacity: 1; pointer-events: all; }

  .an-dr {
    position: fixed; top: 0; right: 0; bottom: 0;
    width: 430px; max-width: 95vw;
    background: var(--an-surface); border-left: 1px solid var(--an-border);
    z-index: 1051; overflow-y: auto;
    transform: translateX(100%);
    transition: transform .3s cubic-bezier(.4,0,.2,1);
    padding: 28px;
  }
  .an-dr.open { transform: translateX(0); }

  .an-dr-hdr {
    display: flex; align-items: center;
    justify-content: space-between; margin-bottom: 24px;
  }
  .an-dr-title { font-size: 17px; font-weight: 700; color: var(--an-text-primary); }
  .an-dr-close {
    width: 32px; height: 32px; border-radius: 8px;
    background: var(--an-surface-2); border: 1px solid var(--an-border);
    color: var(--an-text-secondary); cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    font-size: 16px; opacity: .6; transition: all .18s;
  }
  .an-dr-close:hover { opacity: 1; background: var(--an-accent-soft); color: var(--an-accent); }

  .an-dr-sec {
    margin-bottom: 22px; padding-bottom: 22px;
    border-bottom: 1px solid var(--an-border);
  }
  .an-dr-sec:last-child { border-bottom: none; margin-bottom: 0; padding-bottom: 0; }
  .an-dr-sec-title {
    font-size: 10px; font-weight: 700; text-transform: uppercase;
    letter-spacing: .8px; color: var(--an-text-muted); margin-bottom: 14px;
  }
  .an-dr-row {
    display: flex; justify-content: space-between;
    align-items: flex-start; padding: 6px 0; gap: 12px;
  }
  .an-dr-key { font-size: 12px; color: var(--an-text-muted); flex-shrink: 0; }
  .an-dr-val { font-size: 13px; font-weight: 500; text-align: right; word-break: break-all; color: var(--an-text-primary); }
  .an-dr-val.mono { font-family: monospace; font-size: 11px; color: var(--an-accent); }
  .an-dr-val.green { color: var(--an-green); }
  .an-dr-val.red   { color: var(--an-red); }

  /* Timeline */
  .an-dr-tl { display: flex; flex-direction: column; gap: 0; }
  .an-dr-tli { display: flex; gap: 12px; padding-bottom: 16px; position: relative; }
  .an-dr-tli:last-child { padding-bottom: 0; }
  .an-dr-tli:not(:last-child)::before {
    content: ''; position: absolute; left: 7px; top: 18px;
    width: 2px; bottom: 0; background: var(--an-border);
  }
  .an-dr-tld {
    width: 16px; height: 16px; border-radius: 50%;
    flex-shrink: 0; margin-top: 2px; border: 2px solid;
  }
  .an-dr-tld.done    { background: var(--an-green);  border-color: var(--an-green); }
  .an-dr-tld.current { background: var(--an-accent); border-color: var(--an-accent); box-shadow: 0 0 0 3px var(--an-accent-soft); }
  .an-dr-tld.pnd     { background: transparent; border-color: var(--an-border); }
  .an-dr-tld.fail    { background: var(--an-red); border-color: var(--an-red); }
  .an-dr-tll { font-size: 13px; font-weight: 600; color: var(--an-text-primary); }
  .an-dr-tlt { font-size: 11px; color: var(--an-text-muted); margin-top: 2px; }

  /* ─── Mobile Table Responsive ─── */
  @media (max-width: 992px) {
    .an-rtx-table-wrap { overflow-x: auto; overflow-y: hidden; -webkit-overflow-scrolling: touch; width: 100%; }
    .an-rtx-table { min-width: 600px; font-size: 12px; }
    .an-rtx-table thead tr th { padding: 8px 12px; font-size: 9px; }
    .an-rtx-table tbody td { padding: 10px 12px; font-size: 11px; }
  }

  @media (max-width: 640px) {
    .an-page { padding: 14px 12px 40px; }
    .an-card-body { padding: 14px; }
    .an-card-header { padding: 12px 14px; }
    .an-metric-value { font-size: 28px; }
    .an-page-title { font-size: 20px; }
    .an-rtx-table { min-width: 500px; font-size: 11px; }
    .an-rtx-table thead tr th { padding: 6px 10px; font-size: 8px; }
    .an-rtx-table tbody td { padding: 8px 10px; font-size: 10px; }
  }
`;

/* ─── Avatar helpers ───────────────────────────────────────────────────────── */
const AVATAR_COLORS = [
  "#1d4ed8",
  "#059669",
  "#d97706",
  "#dc2626",
  "#7c3aed",
  "#0891b2",
  "#b45309",
  "#047857",
];
const getAvatarColor = (name = "") => {
  let hash = 0;
  for (let i = 0; i < name.length; i++)
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};
const getInitials = (name = "") =>
  name
    .split(" ")
    .map((n) => n[0])
    .slice(0, 2)
    .join("")
    .toUpperCase() || "–";

/* ─── Animated Counter ─────────────────────────────────────────────────────── */
const useCounter = (target, duration = 1100) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    const numeric = parseFloat(String(target).replace(/[^0-9.]/g, ""));
    if (isNaN(numeric)) {
      setVal(target);
      return;
    }
    let start = null;
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.floor(eased * numeric));
      if (p < 1) requestAnimationFrame(step);
      else setVal(numeric);
    };
    requestAnimationFrame(step);
  }, [target, duration]);
  return val;
};

/* ─── MetricCard ───────────────────────────────────────────────────────────── */
const MetricCard = ({ color, icon, label, value, unit, badge, sub }) => {
  const numericStr = String(value).replace(/[^0-9.]/g, "");
  const counted = useCounter(parseFloat(numericStr) || 0);
  const displayNum = Number.isInteger(counted)
    ? counted.toLocaleString()
    : counted;
  return (
    <div className={`an-metric-card ${color}`}>
      <div className="an-metric-top">
        <div className={`an-metric-icon ${color}`}>
          <i className={`fa ${icon}`} />
        </div>
      </div>
      <div className="an-metric-label">{label}</div>
      <div className="an-metric-value">
        {displayNum}
        {unit && (
          <span
            style={{
              marginLeft: "6px",
              fontSize: "0.65em",
              fontWeight: 700,
              opacity: 0.7,
              letterSpacing: 0,
            }}
          >
            {unit}
          </span>
        )}
      </div>
      <div className="an-metric-footer">
        <span className={`an-metric-badge ${badge.type}`}>
          {badge.type === "up" && (
            <i className="fa fa-arrow-up" style={{ fontSize: 9 }} />
          )}
          {badge.text}
        </span>
        <span className="an-metric-sub">{sub}</span>
      </div>
    </div>
  );
};

/* ─── StatusRow ────────────────────────────────────────────────────────────── */
const StatusRow = ({ label, labelIcon, children }) => (
  <div className="an-status-row">
    <span className="an-status-label">
      <span className="an-status-label-icon">
        <i className={`fa ${labelIcon}`} />
      </span>
      {label}
    </span>
    <div className="an-status-value">{children}</div>
  </div>
);

/* ─── Transaction Drawer ───────────────────────────────────────────────────── */
const TxnDrawer = ({ item, open, onClose }) => {
  if (!item) return null;
  const t = item;
  const status = mapStatus(t.status);
  const isSucceeded = status === "succeeded";
  const isFailed = status === "failed";
  const isProcessing = status === "processing";
  const isPending = status === "pending";
  const displayProductName =
    t.productDetails && t.productDetails.length > 0
      ? t.productDetails[0].productName
      : t.productName || "—";

  return (
    <>
      <div className={`an-dr-ov ${open ? "open" : ""}`} onClick={onClose} />
      <div className={`an-dr ${open ? "open" : ""}`}>
        <div className="an-dr-hdr">
          <span className="an-dr-title">Payment Details</span>
          <button className="an-dr-close" onClick={onClose}>
            <i className="mdi mdi-close" />
          </button>
        </div>

        {/* Identifiers */}
        <div className="an-dr-sec">
          <div className="an-dr-sec-title">Identifiers</div>
          <div className="an-dr-row">
            <span className="an-dr-key">Transaction ID</span>
            <span className="an-dr-val mono">#{t.transactionId}</span>
          </div>
          {t.paymentLinkId && (
            <div className="an-dr-row">
              <span className="an-dr-key">Payment Link ID</span>
              <span className="an-dr-val mono">{t.paymentLinkId}</span>
            </div>
          )}
          {t.subscriptionId && t.subscriptionId !== "0" && (
            <div className="an-dr-row">
              <span className="an-dr-key">Subscription ID</span>
              <span className="an-dr-val mono">{t.subscriptionId}</span>
            </div>
          )}
          <div className="an-dr-row">
            <span className="an-dr-key">Customer ID</span>
            <span className="an-dr-val mono">{t.customerId || "—"}</span>
          </div>
          <div className="an-dr-row">
            <span className="an-dr-key">Customer Email</span>
            <span className="an-dr-val">{t.customerEmail || "—"}</span>
          </div>
          <div className="an-dr-row">
            <span className="an-dr-key">Customer Identity</span>
            <span className="an-dr-val">{t.customerIdentity || "—"}</span>
          </div>
          <div className="an-dr-row">
            <span className="an-dr-key">Payment Type</span>
            <span className="an-dr-val">{t.paymentType || "—"}</span>
          </div>
        </div>

        {/* Transaction */}
        <div className="an-dr-sec">
          <div className="an-dr-sec-title">Transaction</div>
          <div className="an-dr-row">
            <span className="an-dr-key">Product</span>
            <span className="an-dr-val">{displayProductName}</span>
          </div>
          {t.productDetails && t.productDetails.length > 0 && (
            <div className="an-dr-row">
              <span className="an-dr-key">Product Details</span>
              <span className="an-dr-val">
                {t.productDetails[0].qty} x{" "}
                {fmtAmt(
                  t.productDetails[0].itemPrice,
                  t.productDetails[0].currency
                )}
              </span>
            </div>
          )}
          <div className="an-dr-row">
            <span className="an-dr-key">Currency</span>
            <span className="an-dr-val">{t.currency}</span>
          </div>
          <div className="an-dr-row">
            <span className="an-dr-key">Base Amount</span>
            <span className="an-dr-val">
              {fmtAmt(t.baseAmount, t.currency)}
            </span>
          </div>
          {t.discountAmount > 0 && (
            <div className="an-dr-row">
              <span className="an-dr-key">Discount</span>
              <span className="an-dr-val red">
                -{fmtAmt(t.discountAmount, t.currency)}
              </span>
            </div>
          )}
          <div className="an-dr-row">
            <span className="an-dr-key">Fee Amount</span>
            <span className="an-dr-val">{fmtAmt(t.feeAmount, t.currency)}</span>
          </div>
          {t.paidAmount > 0 && (
            <div className="an-dr-row">
              <span className="an-dr-key">Amount Paid</span>
              <span className="an-dr-val green">
                {fmtAmt(t.paidAmount, t.currency)}
              </span>
            </div>
          )}
          {t.storeCrEarned > 0 && (
            <div className="an-dr-row">
              <span className="an-dr-key">Store Credit Earned</span>
              <span className="an-dr-val" style={{ color: "#10b981" }}>
                {fmtAmt(t.storeCrEarned, t.currency)}
              </span>
            </div>
          )}
          {t.storeCrRedeemed > 0 && (
            <div className="an-dr-row">
              <span className="an-dr-key">Store Credit Redeemed</span>
              <span className="an-dr-val" style={{ color: "#818cf8" }}>
                {fmtAmt(t.storeCrRedeemed, t.currency)}
              </span>
            </div>
          )}
          {t.cashbackEarned != 0 && (
            <div className="an-dr-row">
              <span className="an-dr-key">Cashback Earned</span>
              <span className="an-dr-val" style={{ color: "#f59e0b" }}>
                {fmtAmt(t.cashbackEarned, t.currency)}
              </span>
            </div>
          )}
          {t.cashbackRedeemed != 0 && (
            <div className="an-dr-row">
              <span className="an-dr-key">Cashback Redeemed</span>
              <span className="an-dr-val" style={{ color: "#f87171" }}>
                {fmtAmt(t.cashbackRedeemed, t.currency)}
              </span>
            </div>
          )}
          <div className="an-dr-row">
            <span className="an-dr-key">Test Mode</span>
            <span className="an-dr-val">
              {t.testMode === true ? "Yes" : "No"}
            </span>
          </div>
        </div>

        {/* Payment Details */}
        {(t.paymentSubDetails || t.sessionId) && (
          <div className="an-dr-sec">
            <div className="an-dr-sec-title">Payment Details</div>
            {t.paymentMethod && (
              <div className="an-dr-row">
                <span className="an-dr-key">Payment Method</span>
                <span className="an-dr-val">{t.paymentMethod}</span>
              </div>
            )}
            {t.paymentSubDetails && (
              <div className="an-dr-row">
                <span className="an-dr-key">Network</span>
                <span className="an-dr-val">{t.paymentSubDetails}</span>
              </div>
            )}
            {t.sessionId && (
              <div className="an-dr-row">
                <span className="an-dr-key">Session / Address</span>
                <span className="an-dr-val mono">{t.sessionId}</span>
              </div>
            )}
            {t.intentId && (
              <div className="an-dr-row">
                <span className="an-dr-key">Intent ID</span>
                <span className="an-dr-val mono">{t.intentId}</span>
              </div>
            )}
          </div>
        )}

        {/* Status Timeline */}
        <div className="an-dr-sec">
          <div className="an-dr-sec-title">Status Timeline</div>
          <div className="an-dr-tl">
            <div className="an-dr-tli">
              <div className="an-dr-tld done" />
              <div>
                <div className="an-dr-tll">Created</div>
                <div className="an-dr-tlt">{fmtDate(t.paymentCreatedAt)}</div>
              </div>
            </div>
            <div className="an-dr-tli">
              <div
                className={`an-dr-tld ${
                  isSucceeded || isFailed
                    ? "done"
                    : isProcessing
                    ? "current"
                    : "pnd"
                }`}
              />
              <div>
                <div className="an-dr-tll">Processing</div>
                <div className="an-dr-tlt">
                  {isProcessing
                    ? "Awaiting payment confirmation"
                    : t.paymentInitiatedAt
                    ? fmtDate(t.paymentInitiatedAt)
                    : "—"}
                </div>
              </div>
            </div>
            {isFailed && (
              <div className="an-dr-tli">
                <div className="an-dr-tld fail" />
                <div>
                  <div className="an-dr-tll" style={{ color: "var(--an-red)" }}>
                    Failed / Expired
                  </div>
                  <div className="an-dr-tlt">{t.status}</div>
                </div>
              </div>
            )}
            {isSucceeded && (
              <div className="an-dr-tli">
                <div className="an-dr-tld done" />
                <div>
                  <div
                    className="an-dr-tll"
                    style={{ color: "var(--an-green)" }}
                  >
                    Succeeded
                  </div>
                  <div className="an-dr-tlt">
                    {fmtDate(t.paymentProcessAt || t.paymentCreatedAt)}
                  </div>
                </div>
              </div>
            )}
            {status === "refunded" && (
              <div className="an-dr-tli">
                <div
                  className="an-dr-tld"
                  style={{
                    background: "var(--an-accent)",
                    borderColor: "var(--an-accent)",
                  }}
                />
                <div>
                  <div
                    className="an-dr-tll"
                    style={{ color: "var(--an-accent)" }}
                  >
                    Refunded
                  </div>
                  <div className="an-dr-tlt">
                    {fmtDate(t.updated || t.paymentCreatedAt)}
                  </div>
                </div>
              </div>
            )}
            {isPending && (
              <div className="an-dr-tli">
                <div className="an-dr-tld current" />
                <div>
                  <div className="an-dr-tll" style={{ color: "#06b6d4" }}>
                    Awaiting Action
                  </div>
                  <div className="an-dr-tlt">Waiting for customer</div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Subscription info if present */}
        {t.cycle && t.cycle !== "0" && (
          <div className="an-dr-sec">
            <div className="an-dr-sec-title">Subscription Info</div>
            <div className="an-dr-row">
              <span className="an-dr-key">Billing Cycle</span>
              <span className="an-dr-val">{t.cycle}</span>
            </div>
            {t.nextBillingDate && (
              <div className="an-dr-row">
                <span className="an-dr-key">Next Billing</span>
                <span className="an-dr-val">{fmtDate(t.nextBillingDate)}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

/* ─── Recent Transactions Panel ────────────────────────────────────────────── */
const RecentTransactions = () => {
  const [activeTab, setActiveTab] = useState("all");
  const [searchQ, setSearchQ] = useState("");
  const [txnList, setTxnList] = useState([]);
  const [counts, setCounts] = useState({
    all: 0,
    succeeded: 0,
    failed: 0,
    processing: 0,
    refunded: 0,
    test: 0,
  });
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerItem, setDrawerItem] = useState(null);
  const searchTimer = useRef(null);

  const getStatusFilter = (tab) => {
    const map = {
      succeeded: "SUCCESS",
      failed: "FAILED",
      processing: "PROCESSING",
      refunded: "REFUNDED",
    };
    return map[tab] || "";
  };

  const fetchTxns = (overrides = {}) => {
    const merchantId = localStorage.getItem(SITENAMEALIAS + "_merchant_id");
    if (!merchantId) return;
    setLoading(true);

    const tab = overrides.tab !== undefined ? overrides.tab : activeTab;
    const search = overrides.search !== undefined ? overrides.search : searchQ;

    const payload = {
      merchantId: parseInt(merchantId),
      status: getStatusFilter(tab === "test" ? "all" : tab),
      paymentMethod: "",
      network: "",
      search,
      productName: "",
      catalogueName: "",
      subscriptionId: "",
      customerId: "",
      customerIdentity: "",
      currency: "",
      dateRange: "ALL",
      fromDate: "",
      toDate: "",
      page: 1,
      pageSize: 10,
    };

    GetTransactionsByFilter(payload)
      .then((res) => {
        const d = res.data;
        let list = d.transactions || [];

        /* client-side test filter */
        if (tab === "test") list = list.filter((t) => t.testMode === true);

        setTxnList(list);
        setCounts({
          all: d.totalCount || 0,
          succeeded: d.succeededCount || 0,
          failed: d.failedCount || 0,
          processing: 0,
          refunded: d.refundedCount || 0,
          test: (d.transactions || []).filter((t) => t.testMode === true)
            .length,
        });
      })
      .catch(() => setTxnList([]))
      .finally(() => setLoading(false));
  };

  /* initial load */
  useEffect(() => {
    fetchTxns();
  }, []);

  /* tab change */
  const handleTab = (tab) => {
    setActiveTab(tab);
    fetchTxns({ tab });
  };

  /* debounced search */
  const handleSearch = (val) => {
    setSearchQ(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => fetchTxns({ search: val }), 400);
  };

  const tabs = [
    { key: "all", label: "All", count: counts.all },
    { key: "succeeded", label: "Success", count: counts.succeeded },
    { key: "failed", label: "Failed", count: counts.failed },
    { key: "processing", label: "Processing", count: counts.processing },
    { key: "refunded", label: "Refunded", count: counts.refunded },
    { key: "test", label: "Test User", count: counts.test, isTest: true },
  ];

  return (
    <>
      <div className="an-card" style={{ animationDelay: "0.32s" }}>
        {/* Header */}
        <div
          className="an-card-header"
          style={{ flexDirection: "column", alignItems: "stretch", gap: 14 }}
        >
          {/* Title row */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 10,
            }}
          >
            <h3 className="an-card-title" style={{ margin: 0 }}>
              <span className="an-card-title-icon">
                <i className="fa fa-list-alt" />
              </span>
              Recent Transactions
            </h3>
            <span
              style={{
                fontSize: 12,
                color: "var(--an-text-muted)",
                fontWeight: 500,
              }}
            >
              Showing {txnList.length} of {counts.all} entries
            </span>
          </div>

          {/* Combined row: tabs + search */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
            }}
          >
            <div
              className="an-rtx-tabs"
              style={{ minWidth: 0, marginBottom: 0 }}
            >
              {tabs.map((t) => (
                <button
                  key={t.key}
                  className={`an-rtx-tab${t.isTest ? " test" : ""}${
                    activeTab === t.key ? " active" : ""
                  }`}
                  onClick={() => handleTab(t.key)}
                >
                  {t.label}
                  <span className="an-rtx-tab-count">{t.count}</span>
                </button>
              ))}
            </div>

            <div
              className="an-rtx-search-wrap"
              style={{ width: "280px", flexShrink: 0 }}
            >
              <i className="fa fa-search an-rtx-search-icon" />
              <input
                className="an-rtx-search"
                placeholder="Search by customer email, txn id and product name."
                value={searchQ}
                onChange={(e) => handleSearch(e.target.value)}
              />
              {searchQ && (
                <button
                  className="an-rtx-search-clear"
                  onClick={() => handleSearch("")}
                >
                  <i className="fa fa-times" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="an-rtx-table-wrap">
          <table className="an-rtx-table">
            <thead>
              <tr>
                <th>Txn ID</th>
                <th>Product / Type</th>
                <th>Amount</th>
                <th>Method</th>
                <th>Status</th>
                <th>Date</th>
                <th style={{ width: 45 }}></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7}>
                    <div className="an-rtx-empty">
                      <i className="fa fa-spinner fa-spin" />
                      Loading transactions...
                    </div>
                  </td>
                </tr>
              ) : txnList.length === 0 ? (
                <tr>
                  <td colSpan={7}>
                    <div className="an-rtx-empty">
                      <i className="fa fa-inbox" />
                      No transactions found.
                    </div>
                  </td>
                </tr>
              ) : (
                txnList.map((t) => {
                  const status = mapStatus(t.status);
                  const displayProductName =
                    t.productDetails && t.productDetails.length > 0
                      ? t.productDetails[0].productName
                      : t.productName || "—";
                  const paymentType = t.paymentType || "ONE-TIME";

                  return (
                    <tr
                      key={t.transactionId}
                      onClick={() => {
                        setDrawerItem(t);
                        setDrawerOpen(true);
                      }}
                    >
                      <td>
                        <span className="an-rtx-txnid">#{t.transactionId}</span>
                        {t.testMode === true && (
                          <div>
                            <span className="an-rtx-tag test">TEST</span>
                          </div>
                        )}
                      </td>
                      <td>
                        <div className="an-rtx-product">
                          {displayProductName}
                        </div>
                        <span
                          className={`an-rtx-tag ${
                            paymentType === "SUBSCRIPTION" ? "sub" : "onetime"
                          }`}
                        >
                          {paymentType}
                        </span>
                      </td>
                      <td>
                        <div className="an-rtx-amount">
                          {fmtAmt(
                            (t.baseAmount || 0) + (t.feeAmount || 0),
                            t.currency
                          )}
                          <div
                            style={{
                              display: "flex",
                              gap: 4,
                              marginTop: 4,
                              flexWrap: "wrap",
                            }}
                          >
                            {t.storeCrEarned > 0 && (
                              <div
                                style={{
                                  background: "rgba(16,185,129,0.1)",
                                  color: "#10b981",
                                  fontSize: 9,
                                  padding: "1px 4px",
                                  borderRadius: 4,
                                  fontWeight: 700,
                                }}
                                title={`Store Credit Earned: ${fmtAmt(
                                  t.storeCrEarned,
                                  t.currency
                                )}`}
                              >
                                SC+
                              </div>
                            )}
                            {t.storeCrRedeemed > 0 && (
                              <div
                                style={{
                                  background: "rgba(129,140,248,0.1)",
                                  color: "#818cf8",
                                  fontSize: 9,
                                  padding: "1px 4px",
                                  borderRadius: 4,
                                  fontWeight: 700,
                                }}
                                title={`Store Credit Redeemed: ${fmtAmt(
                                  t.storeCrRedeemed,
                                  t.currency
                                )}`}
                              >
                                SC-
                              </div>
                            )}
                            {t.cashbackEarned != 0 && (
                              <div
                                style={{
                                  background: "rgba(245,158,11,0.1)",
                                  color: "#f59e0b",
                                  fontSize: 9,
                                  padding: "1px 4px",
                                  borderRadius: 4,
                                  fontWeight: 700,
                                }}
                                title={`Cashback Earned: ${fmtAmt(
                                  t.cashbackEarned,
                                  t.currency
                                )}`}
                              >
                                CB+
                              </div>
                            )}
                            {t.cashbackRedeemed != 0 && (
                              <div
                                style={{
                                  background: "rgba(239,68,68,0.1)",
                                  color: "#f87171",
                                  fontSize: 9,
                                  padding: "1px 4px",
                                  borderRadius: 4,
                                  fontWeight: 700,
                                }}
                                title={`Cashback Redeemed: ${fmtAmt(
                                  t.cashbackRedeemed,
                                  t.currency
                                )}`}
                              >
                                CB-
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="an-rtx-amtsub">
                          Base: {fmtAmt(t.baseAmount, t.currency)} · Fee:{" "}
                          {fmtAmt(t.feeAmount, t.currency)}
                        </div>
                      </td>
                      <td>
                        <div className="an-rtx-method">
                          {t.paymentMethod || "—"}
                        </div>
                        {t.paymentSubDetails && (
                          <div
                            style={{
                              fontSize: 11,
                              color: "var(--an-text-muted)",
                              marginTop: 2,
                            }}
                          >
                            {t.paymentSubDetails}
                          </div>
                        )}
                      </td>
                      <td>
                        <span className={`an-rtx-pill ${status}`}>
                          {statusLabel(t.status)}
                        </span>
                      </td>
                      <td className="an-rtx-date">
                        {fmtDate(t.paymentCreatedAt || t.txnDate)}
                      </td>
                      <td>
                        <div className="an-rtx-arrow">
                          <i className="mdi mdi-chevron-right" />
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="an-rtx-footer">
          <span className="an-rtx-count">
            Showing {txnList.length} of {counts.all.toLocaleString()}{" "}
            transactions
          </span>
          <a href="/transactions" className="an-rtx-viewall">
            View All <i className="fa fa-arrow-right" />
          </a>
        </div>
      </div>

      {/* Drawer */}
      <TxnDrawer
        item={drawerItem}
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setDrawerItem(null);
        }}
      />
    </>
  );
};

/* ─── Main Analytics Component ─────────────────────────────────────────────── */
const Analytics = () => {
  const isReadOnly = currentPageAccess() === "READ";
  const blockReadOnly = (e) => {
    if (isReadOnly) {
      e.preventDefault();
      e.stopPropagation();
      showToast("error", "You have read-only access to this page");
    }
  };
  const [theme, setTheme] = useState("light");
  const [showLoader, setShowLoader] = useState(false);
  const [metricLoading, setMetricLoading] = useState(true);
  const [metricData, setMetricData] = useState(null);
  const [accountData, setAccountData] = useState({
    status: "Active",
    withdrawalTier: 1,
    remainingBeforeKyc: "$5,000",
    kycUsedPercent: 60,
    bankInfo: false,
    walletAddresses: "",
    bankDetailsStatus: null,
  });

  /* ── Theme detection ── */
  useEffect(() => {
    const detectTheme = () => {
      const stored = localStorage.getItem(SITENAMEALIAS + "_theme") || "dark";
      setTheme(stored === "dark" ? "dark" : "light");
    };
    detectTheme();
    const observer = new MutationObserver(detectTheme);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class", "data-theme", "style"],
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class", "data-theme", "style"],
    });
    const interval = setInterval(detectTheme, 500);
    const timeout = setTimeout(() => clearInterval(interval), 5000);
    return () => {
      observer.disconnect();
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, []);

  /* ── Fetch metric data ── */
  const fetchMetricData = () => {
    const merchantId = localStorage.getItem(SITENAMEALIAS + "_merchant_id");
    if (!merchantId) return;
    setMetricLoading(true);
    DashboardPaymentHistory(merchantId)
      .then((res) => {
        setMetricLoading(false);
        if (res.data.status === "success") setMetricData(res.data);
        else
          showToast(
            "error",
            res.data.message || "Failed to fetch payment history"
          );
      })
      .catch((err) => {
        setMetricLoading(false);
        showHttpError(err);
      });
  };

  /* ── Fetch account data ── */
  const fetchAccountData = () => {
    setShowLoader(true);
    FetchMerchantStatus({
      merchant_id: localStorage.getItem(SITENAMEALIAS + "_merchant_id"),
    })
      .then((res) => {
        const response = res.data[0];
        if (response.error !== "0") {
          showToast("error", response.error_msg);
          setShowLoader(false);
          return;
        }
        setAccountData((prev) => ({
          ...prev,
          status: response.basic_verification_submitted,
          walletAddresses: response.crypto_address_added,
          remainingBeforeKyc: response.remaining_withdrawable_amt,
        }));
        GetUserDetails()
          .then((res) => {
            setAccountData((prev) => ({
              ...prev,
              withdrawalTier: res.data.userTierType,
              bankDetailsStatus: res.data.bankDetailsStatus,
            }));
            setShowLoader(false);
          })
          .catch((err) => {
            setShowLoader(false);
            showHttpError(err);
          });
      })
      .catch((err) => {
        setShowLoader(false);
        showHttpError(err);
      });
  };

  /* ── Assets (needed for other parts of the app) ── */
  useEffect(() => {
    fetchMetricData();
    fetchAccountData();
    const payload = {
      merchant_id: localStorage.getItem(SITENAMEALIAS + "_merchant_id"),
    };
    FetchUsdBtcLedgerAmount(payload)
      .then((res) => {
        const response = res.data[0];
        if (response.error !== "0") return;
        const arr = (response.coin_balance || [])
          .filter((c) => c.currency_code !== "USD")
          .map((c) => ({
            assetName: c.currency_name,
            coinBalance: c.balance,
            assetCode: c.currency_code,
            assetId: c.currency_id,
            assetImage: c.logo,
            network: c.network,
            isBrokerCurrency: c.is_broker_currency,
          }));
        localStorage.setItem(SITENAMEALIAS + "_assets", JSON.stringify(arr));
      })
      .catch(() => {});
  }, []);

  const today = new Date().toLocaleDateString("en-US", {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
  });
  const currencySymbol =
    localStorage.getItem(SITENAMEALIAS + "_home_currency") || "$";

  return (
    <>
      <style>{STYLES}</style>
      <div className="container-scroller">
        <Sidebar />
        <Header />
        <div className="main-panel">
          <div className="an-page" data-theme={theme}>
            {/* ── Page Header ── */}
            <div className="an-page-header">
              <PageHeader
                icon="mdi-view-dashboard"
                title="Dashboard"
                subtitle="Track revenue, manage actions, and monitor activity."
              />
              <div className="an-header-right">
                <div className="an-date-badge">
                  <span className="an-live-dot" />
                  {today}
                </div>
              </div>
            </div>

            {/* ── Metric Cards ── */}
            <div className="an-metrics-grid">
              {metricLoading ? (
                ["blue", "green", "gold"].map((c) => (
                  <div key={c} className={`an-metric-card ${c}`}>
                    <div className="an-metric-top">
                      <div className={`an-metric-icon ${c}`}>
                        <i className="fa fa-spinner fa-spin" />
                      </div>
                    </div>
                    <div className="an-metric-label">Loading...</div>
                    <div className="an-metric-value">—</div>
                    <div className="an-metric-footer">
                      <span className="an-metric-badge neutral">Loading</span>
                      <span className="an-metric-sub">Please wait</span>
                    </div>
                  </div>
                ))
              ) : metricData ? (
                <>
                  <MetricCard
                    color="blue"
                    icon="fa-line-chart"
                    label={
                      metricData.volumeDetails?.[0]?.labelText ||
                      "TOTAL PROCESSED (ALL-TIME)"
                    }
                    value={`${currencySymbol}${
                      metricData.volumeDetails?.[0]?.value?.toLocaleString() ||
                      0
                    }`}
                    unit={
                      localStorage.getItem(SITENAMEALIAS + "_home_currency") ||
                      "USD"
                    }
                    badge={{
                      type:
                        metricData.volumeDetails?.[0]?.subTextValue > 0
                          ? "up"
                          : "neutral",
                      text: `${
                        metricData.volumeDetails?.[0]?.subTextValue > 0
                          ? "+"
                          : ""
                      }${metricData.volumeDetails?.[0]?.subTextValue || 0}`,
                    }}
                      sub="Change in last 24 H"
                  />
                  <MetricCard
                    color="green"
                    icon="fa-check-circle"
                    label={
                      metricData.paymentDetails?.[0]?.labelText ||
                      "Successful Transactions"
                    }
                    value={
                      metricData.paymentDetails?.[0]?.value?.toLocaleString() ||
                      0
                    }
                    unit=""
                    badge={{
                      type:
                        metricData.paymentDetails?.[0]?.subTextValue > 0
                          ? "up"
                          : "neutral",
                      text:
                        metricData.paymentDetails?.[0]?.subTextValue > 0
                          ? `Success Rate: ${metricData.paymentDetails[0].subTextValue}%`
                          : "No transactions",
                    }}
                    sub="Out of total attempts"
                  />
                  <div className={`an-metric-card gold`}>
                    <div className="an-metric-top">
                      <div className={`an-metric-icon gold`}>
                        <i className="fa fa-university" />
                      </div>
                    </div>
                    <div className="an-metric-label">
                      {metricData.balanceDetails?.[0]?.labelText ||
                        "Available for Withdrawal"}
                    </div>
                    <div className="an-metric-value">
                      {`${
                        metricData.balanceDetails?.[0]?.value?.toLocaleString() ||
                        0
                      }`}
                      <span
                        style={{
                          marginLeft: "6px",
                          fontSize: "0.65em",
                          fontWeight: 700,
                          opacity: 0.7,
                          letterSpacing: 0,
                        }}
                      >
                        {localStorage.getItem(
                          SITENAMEALIAS + "_home_currency"
                        ) || "USD"}
                      </span>
                    </div>
                    <div className="an-metric-footer">
                      <Link
                        to={isReadOnly ? "#" : "/wallet"}
                        onClick={blockReadOnly}
                        style={{
                          textDecoration: "none",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "4px",
                          background: "var(--an-accent)",
                          color: "#fff",
                          padding: "4px 12px",
                          borderRadius: "20px",
                          fontSize: "11.5px",
                          fontWeight: "700",
                          cursor: isReadOnly ? "not-allowed" : "pointer",
                          opacity: isReadOnly ? 0.5 : 1,
                        }}
                      >
                        <i
                          className="fa fa-arrow-right"
                          style={{ fontSize: 10 }}
                        />{" "}
                        Ready to withdraw
                      </Link>
                      <span className="an-metric-sub">Settled balance</span>
                    </div>
                  </div>
                </>
              ) : (
                ["blue", "green", "gold"].map((c, i) => (
                  <MetricCard
                    key={c}
                    color={c}
                    icon={
                      ["fa-line-chart", "fa-check-circle", "fa-university"][i]
                    }
                    label={
                      [
                        "TOTAL PROCESSED (ALL-TIME)",
                        "Successful Transactions",
                        "Available for Withdrawal",
                      ][i]
                    }
                    value={`${currencySymbol}0`}
                    unit=""
                    badge={{ type: "neutral", text: "0" }}
                    sub="Change in last 24 H"
                  />
                ))
              )}
            </div>

            {/* ── Action Center ── */}
            <div className="an-action-grid">
              <div
                className="an-card"
                style={{ gridColumn: "span 2", animationDelay: "0.18s" }}
              >
                <div className="an-card-header">
                  <h3 className="an-card-title">
                    <span className="an-card-title-icon">
                      <i className="fa fa-bolt" />
                    </span>
                    Quick Actions
                  </h3>
                  <span
                    style={{
                      fontSize: 11.5,
                      color: "var(--an-text-muted)",
                      fontWeight: 500,
                    }}
                  >
                    Click to proceed
                  </span>
                </div>
                <div className="an-card-body">
                  <div className="an-action-buttons">
                    {[
                      {
                        icon: "fa-credit-card",
                        label: "Create Payment",
                        sub: "Payment button / link",
                        to: "/create-payment",
                      },
                      {
                        icon: "fa-repeat",
                        label: "Create Product",
                        sub: "Recurring billing",
                        to: "/product-catalog",
                      },
                      {
                        icon: "fa-arrow-up",
                        label: "Withdraw Funds",
                        sub: "To wallet or bank",
                        to: "/wallet",
                      },
                    ].map((a) => (
                      <Link
                        key={a.label}
                        className="an-action-btn"
                        to={isReadOnly ? "#" : a.to}
                        onClick={blockReadOnly}
                        style={
                          isReadOnly
                            ? { opacity: 0.5, cursor: "not-allowed" }
                            : undefined
                        }
                      >
                        <div className="an-abt-icon">
                          <i className={`fa ${a.icon}`} />
                        </div>
                        <div className="an-abt-text">
                          <div className="an-abt-label">{a.label}</div>
                          <div className="an-abt-sub">{a.sub}</div>
                        </div>
                        <div className="an-abt-arrow">
                          <i className="fa fa-arrow-right" />
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>

              <div className="an-card" style={{ animationDelay: "0.25s" }}>
                <div className="an-card-header">
                  <h3 className="an-card-title">
                    <span className="an-card-title-icon">
                      <i className="fa fa-shield" />
                    </span>
                    Account Status
                  </h3>
                </div>
                <div
                  className="an-card-body"
                  style={{ paddingTop: 8, paddingBottom: 8 }}
                >
                  <div className="an-status-list">
                    <StatusRow label="Account Status" labelIcon="fa-circle">
                      <span
                        className={
                          accountData.status === 1
                            ? "an-badge active"
                            : "an-badge warning"
                        }
                      >
                        <span className="an-badge-dot" />
                        {accountData.status === 1 ? "Active" : "Not Active"}
                      </span>
                    </StatusRow>
                    <StatusRow label="Withdrawal Tier" labelIcon="fa-trophy">
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-end",
                          gap: 5,
                        }}
                      >
                        <span className="an-badge info">
                          Tier {accountData.withdrawalTier}
                        </span>
                        {accountData.withdrawalTier < 3 && (
                          <a
                            href={
                              isReadOnly
                                ? "#"
                                : "/identity-verification"
                            }
                            onClick={blockReadOnly}
                            className="an-link-btn"
                            style={
                              isReadOnly
                                ? {
                                    fontSize: 11,
                                    opacity: 0.5,
                                    cursor: "not-allowed",
                                  }
                                : { fontSize: 11 }
                            }
                          >
                            <i className="fa fa-arrow-up" /> Complete Tier{" "}
                            {parseInt(accountData.withdrawalTier) + 1}
                          </a>
                        )}
                      </div>
                    </StatusRow>
                    <StatusRow
                      label="Withdrawable Limit"
                      labelIcon="fa-bar-chart"
                    >
                      <div className="an-progress-wrap">
                        <span
                          className="an-badge warning"
                          style={{ fontSize: 11 }}
                        >
                          {accountData.remainingBeforeKyc}{" "}
                          {localStorage.getItem(
                            SITENAMEALIAS + "_home_currency"
                          )}
                        </span>
                      </div>
                    </StatusRow>
                    <StatusRow
                      label="Settlement Bank"
                      labelIcon="fa-university"
                    >
                      {accountData.bankDetailsStatus === "0" ? (
                        <a
                          href={isReadOnly ? "#" : "/bank-info"}
                          onClick={blockReadOnly}
                          className="an-link-btn"
                          style={
                            isReadOnly
                              ? { opacity: 0.5, cursor: "not-allowed" }
                              : undefined
                          }
                        >
                          <i className="fa fa-plus" /> Submitted
                        </a>
                      ) : accountData.bankDetailsStatus === "2" ? (
                        <span className="an-link-btn done">
                          <i className="fa fa-check" /> Completed
                        </span>
                      ) : accountData.bankDetailsStatus === "3" ? (
                        <a
                          href={isReadOnly ? "#" : "/bank-info"}
                          onClick={blockReadOnly}
                          className="an-link-btn"
                          style={
                            isReadOnly
                              ? { opacity: 0.5, cursor: "not-allowed" }
                              : undefined
                          }
                        >
                          <i className="fa fa-times" /> Rejected
                        </a>
                      ) : (
                        <a
                          href={isReadOnly ? "#" : "/bank-info"}
                          onClick={blockReadOnly}
                          className="an-link-btn"
                          style={
                            isReadOnly
                              ? { opacity: 0.5, cursor: "not-allowed" }
                              : undefined
                          }
                        >
                          <i className="fa fa-plus" /> Fill Bank Info
                        </a>
                      )}
                    </StatusRow>
                    <StatusRow label="Settlement Wallet" labelIcon="fa-bitcoin">
                      {accountData.walletAddresses !== "" ? (
                        <span className="an-link-btn done">
                          <i className="fa fa-check" /> Completed
                        </span>
                      ) : (
                        <a
                          href={isReadOnly ? "#" : "/crypto-address"}
                          onClick={blockReadOnly}
                          className="an-link-btn"
                          style={
                            isReadOnly
                              ? { opacity: 0.5, cursor: "not-allowed" }
                              : undefined
                          }
                        >
                          <i className="fa fa-plus" /> Add Wallet
                        </a>
                      )}
                    </StatusRow>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Recent Transactions ── */}
            <RecentTransactions />
          </div>
          <Footer />
        </div>
      </div>
      <Loader show={showLoader} />
    </>
  );
};

export default Analytics;
